import { useForm } from '@inertiajs/react'
import { type FormEvent } from 'react'
import BorrowerLayout from '@/layouts/BorrowerLayout'

interface Props {
  email_sent?: boolean
  email?: string
}

export default function Login({ email_sent = false, email = '' }: Props) {
  const { data, setData, post, processing } = useForm({ email: '' })

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    post('/my-loans/login')
  }

  if (email_sent) {
    return (
      <BorrowerLayout>
        <div className="max-w-md mx-auto mt-12 sm:mt-20 text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center">
            <svg className="w-8 h-8 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
            </svg>
          </div>
          <h1 className="mt-6 text-2xl font-bold text-gray-900">Check your email</h1>
          <p className="mt-3 text-gray-600 leading-relaxed">
            We sent a login link to <strong className="text-gray-900">{email}</strong>.
            Click the link to securely log in to your DebtLens dashboard.
          </p>
          <p className="mt-6 text-sm text-gray-400">
            Link expires in 15 minutes. Check your spam folder if you don't see it.
          </p>
        </div>
      </BorrowerLayout>
    )
  }

  return (
    <BorrowerLayout>
      <div className="max-w-md mx-auto mt-12 sm:mt-20">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900">Log in to DebtLens</h1>
          <p className="mt-2 text-gray-600">
            Enter your email and we'll send you a secure login link.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="mt-8 space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email address
            </label>
            <input
              id="email"
              type="email"
              value={data.email}
              onChange={(e) => setData('email', e.target.value)}
              placeholder="you@example.com"
              required
              autoFocus
              className="w-full py-2.5 px-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            />
          </div>

          <button
            type="submit"
            disabled={processing}
            className="w-full py-2.5 px-4 rounded-lg text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 transition-colors disabled:opacity-50"
          >
            {processing ? 'Sending...' : 'Send login link'}
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-gray-400">
          No password needed. We'll email you a secure link.
        </p>

        <p className="mt-3 text-center text-xs text-gray-400">
          By signing in, you acknowledge our{' '}
          <a href="/privacy" className="text-emerald-600 hover:text-emerald-700">
            Privacy Policy
          </a>
          .
        </p>
      </div>
    </BorrowerLayout>
  )
}
